import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { CorrectionNeededBehaviorComponent } from './correction-needed-behavior/correction-needed-behavior.component';
import { CorrectionNeededAssessmentComponent } from './correction-needed-assessment/correction-needed-assessment.component';
import { CorrectionNeededReviewEmailComponent } from './correction-needed-review-email/correction-needed-review-email.component';
import { CorrectionNeededStrategyComponent } from './correction-needed-strategy/correction-needed-strategy.component';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import {
  BehaviorInputData,
  CorrectionNeededInterface,
  FidelityCheckModel,
  FidelityReviewResult,
  FormEventEmit,
  sendEmailPayload,
} from '../../fidelity.interface';
import { FidelityCheckService } from '../../fidelity.service';
import { CommonService } from '../../../../shared/services/common.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { DrawerService } from '../../../../core/services/drawer.service';
import { InsightTerm, TabIndex, ReviewStatus } from '../../fidelity.enums';
import { CommonModule } from '@angular/common';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ErrorResponse } from '../../../../core/models/error.models';
import { CorrectionNeededDataComponent } from './correction-needed-data/correction-needed-data.component';
import { LabelKey } from '../../../../core/models/report-labels.model';
import { ReportLabelsService } from '../../../manage-settings/report-labels/report-labels.service';
import { AuditContextService } from '../../../audit-logs/audit-context.service';
import { AuditFieldChange } from '../../../audit-logs/audit-logs.interface';

@Component({
  selector: 'app-fidelity-check-correction-needed',
  standalone: true,
  imports: [
    NzFormModule,
    NzTabsModule,
    NzButtonModule,
    CorrectionNeededBehaviorComponent,
    CorrectionNeededAssessmentComponent,
    CorrectionNeededReviewEmailComponent,
    CorrectionNeededStrategyComponent,
    CorrectionNeededDataComponent,
    CommonModule,
    NzSpinModule,
  ],
  templateUrl: './fidelity-check-correction-needed.component.html',
})
export class FidelityCheckCorrectionNeededComponent implements OnDestroy {
  loading$: Observable<boolean> = of(false);
  private readonly destroy$ = new Subject<void>();
  drawerParams: CorrectionNeededInterface | null = null;
  fidelityData: FidelityReviewResult | null = null;
  selectedTabIndex = 0;
  selectedTab = 'Behavior';
  behvaiorInputData = {} as BehaviorInputData;
  fidelityPaylod!: FidelityCheckModel;
  errorMessage!: string;
  emailPayload!: sendEmailPayload;
  isFormValid = false;
  tabIndex = TabIndex;
  reviewStatus = ReviewStatus;
  fidelityCheckLabel: string = LabelKey.FIDELITY_CHECK;
  constructor(
    private readonly drawerService: DrawerService,
    private readonly loaderService: LoaderService,
    private readonly fidelityService: FidelityCheckService,
    private readonly commonService: CommonService,
    private readonly cd: ChangeDetectorRef,
    private readonly reportLabelsService: ReportLabelsService,
    private readonly auditContextService: AuditContextService
  ) {
    this.reportLabelsService
      .getLabelValue(LabelKey.FIDELITY_CHECK)
      .subscribe(value => {
        this.fidelityCheckLabel = value;
      });
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerParams();
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<CorrectionNeededInterface>(
        DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParams = params;
          this.getfidelityByBehvaiorIdandType();
        }
      });
  }

  async onSelectTab(index: number) {
    this.selectedTabIndex = index;
    this.selectedTab = this.getVisibleTabs()[index] || '';

    if (this.selectedTab !== 'Review Email') {
      this.setBehvaiorData();
    }
  }

  private getVisibleTabs(): string[] {
    const visibleTabs: string[] = [];

    const tabConditions = [
      { key: 'Behavior', data: this.fidelityData?.behavior },
      { key: 'Assesment', data: this.fidelityData?.assesment },
      { key: 'Strategy', data: this.fidelityData?.stretagy },
      { key: 'Data', data: this.fidelityData?.data },
    ];

    tabConditions.forEach(tab => {
      if (
        tab.data &&
        (tab.data.reviewStatusId === this.reviewStatus['Changes Requested'] ||
          tab.data.reviewStatusId === this.reviewStatus['Changes Received'])
      ) {
        visibleTabs.push(tab.key);
      }
    });

    visibleTabs.push('Review Email'); // Review Email is always last
    return visibleTabs;
  }

  isTabVisible(tabName: string): boolean {
    const tabMapping: Record<string, { reviewStatusId: number } | undefined> = {
      Behavior: this.fidelityData?.behavior,
      Assesment: this.fidelityData?.assesment,
      Strategy: this.fidelityData?.stretagy,
      Data: this.fidelityData?.data,
    };

    const tabData = tabMapping[tabName];
    if (tabData) {
      return (
        tabData.reviewStatusId === this.reviewStatus['Changes Requested'] ||
        tabData.reviewStatusId === this.reviewStatus['Changes Received']
      );
    }

    return false;
  }

  getfidelityByBehvaiorIdandType() {
    if (this.drawerParams?.behavior?.id) {
      this.loaderService.show();
      this.fidelityService
        .getFidelityBybehvaiorIdandType(this.drawerParams.behavior?.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            this.loaderService.hide();
            if (response.result) {
              this.fidelityData = response.result;
              this.selectedTab = this.getVisibleTabs()[0] || '';
              this.selectedTabIndex = 0;
              this.setBehvaiorData();
              this.cd.detectChanges();
            }
          },
          error: error => {
            this.loaderService.hide();
            this.commonService.notify('error', error.message);
          },
        });
    }
  }

  setBehvaiorData() {
    this.behvaiorInputData = {
      selectedTab: this.selectedTab,
      selectedTabIndex: this.selectedTabIndex,
      caseBehvaiorId: this.drawerParams?.behavior?.id ?? 0,
      fidelityData: this.fidelityData,
      caseDetail: this.drawerParams?.caseDetail || null,
    };
  }

  formValueChange(event: FormEventEmit): void {
    this.fidelityPaylod = event.formValue;
    this.isFormValid = event.isFormValid;
  }

  onCancel() {
    this.drawerService.closeDrawer(
      DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
    );
  }
  saveandClose() {
    this.saveFidelityCheck(true);
  }

  saveFidelityCheck(isClose = false): void {
    this.fidelityPaylod['caseBehaviourId'] =
      this.drawerParams?.behavior?.id ?? 0;
    const isEditMode = !!this.fidelityPaylod?.id;
    const excludeFields = [
      'id',
      'adminNotes',
      'reviewedByAdminUserId',
      'dateModifiedByAdmin',
      'reviewStatusId',
    ];

    let modifiedFields: AuditFieldChange[] = [];
    if (isEditMode) {
      modifiedFields = this.buildModifiedFieldsWithAuditContext({
        newValue: this.fidelityPaylod as unknown as Record<string, unknown>,
        oldValue: this.getCurrentTabSection(),
        exclude: excludeFields,
      });
    } else {
      modifiedFields = this.buildModifiedFieldsWithAuditContext({
        newValue: this.fidelityPaylod as unknown as Record<string, unknown>,
        oldValue: undefined,
        exclude: excludeFields,
      });
    }

    (
      this.fidelityPaylod as unknown as {
        modifiedFields?: AuditFieldChange[];
      }
    ).modifiedFields = modifiedFields;
    this.fidelityPaylod._modifiedFields = modifiedFields;

    this.loaderService.show();
    const operation$ = isEditMode
      ? this.fidelityService.updateFidelityCheck(
          this.fidelityPaylod,
          this.fidelityPaylod.id ?? 0
        )
      : this.fidelityService.createFidelityCheck(this.fidelityPaylod);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.getfidelityByBehvaiorIdandType();
          this.commonService.notify(
            'success',
            `${this.fidelityCheckLabel} ${isEditMode ? 'updated' : 'created'} successfully`
          );
          if (isClose)
            this.drawerService.closeDrawer(
              DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
            );
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  private getCurrentTabSection(): Record<string, unknown> {
    if (!this.fidelityData) {
      return {};
    }

    const tabKey = this.selectedTab.toLowerCase();
    const section = (this.fidelityData as unknown as Record<string, unknown>)[
      tabKey
    ];

    if (section && typeof section === 'object') {
      return section as Record<string, unknown>;
    }

    return {};
  }

  private getOldValueForKey(
    key: string,
    oldValue: Record<string, unknown> | undefined,
    newValue: Record<string, unknown>
  ): unknown {
    if (oldValue) {
      return oldValue[key];
    }
    if (key === 'reviewType') {
      return newValue[key];
    }
    return undefined;
  }

  private processReviewScoreTypeId(
    oldValue: Record<string, unknown>,
    newValue: Record<string, unknown>
  ): { oldVal: unknown; newVal: unknown; hasChanged: boolean } {
    let oldId: number | undefined;
    let oldText: string | undefined;

    if (
      oldValue['reviewScoreType'] &&
      typeof oldValue['reviewScoreType'] === 'object'
    ) {
      const oldReviewScoreType = oldValue['reviewScoreType'] as {
        id?: number;
        reviewText?: string;
      };
      oldId = oldReviewScoreType.id;
      oldText = oldReviewScoreType.reviewText;
    } else if (typeof oldValue['reviewScoreTypeId'] === 'number') {
      oldId = oldValue['reviewScoreTypeId'];
    }

    const newId =
      typeof newValue['reviewScoreTypeId'] === 'number'
        ? newValue['reviewScoreTypeId']
        : undefined;

    const hasChanged = oldId !== newId;
    const oldVal = oldText ?? oldId ?? '';
    const newVal = newId === oldId ? (oldText ?? newId ?? '') : (newId ?? '');

    return { oldVal, newVal, hasChanged };
  }

  private enrichFieldValue(
    key: string,
    val: unknown,
    currentValue: Record<string, unknown>,
    compareValue: Record<string, unknown>
  ): unknown {
    if (key === 'reviewType') {
      return val ? InsightTerm[val as number] || val : null;
    }

    if (key === 'caseBehaviourId' || key === 'caseBehvaiorId') {
      return val ?? null;
    }

    if (key === 'reviewScoreTypeId') {
      const oldReviewScoreType = currentValue['reviewScoreType'] as
        | { id?: number; reviewText?: string }
        | undefined;
      if (oldReviewScoreType?.id && oldReviewScoreType.id === val) {
        return oldReviewScoreType.reviewText ?? val;
      }
      return val ?? null;
    }

    if (key === 'changesApplied') {
      return this.enrichChangesAppliedValue(val, currentValue, compareValue);
    }

    return val;
  }

  private enrichChangesAppliedValue(
    val: unknown,
    currentValue: Record<string, unknown>,
    compareValue: Record<string, unknown>
  ): unknown {
    const currentReviewStatusId = currentValue['reviewStatusId'];
    const compareReviewStatusId = compareValue['reviewStatusId'];

    if (
      currentReviewStatusId === this.reviewStatus['Changes Received'] ||
      compareReviewStatusId === this.reviewStatus['Changes Received']
    ) {
      if (val === null || val === undefined || val === '') {
        return 'No';
      }
      return val === true ? 'Yes' : 'No';
    }

    if (val === null || val === undefined || val === '') {
      return '';
    }

    return val;
  }

  private buildEnrichedValues(
    allKeys: string[],
    oldValue: Record<string, unknown>,
    newValue: Record<string, unknown>
  ): {
    enrichedOld: Record<string, unknown>;
    enrichedNew: Record<string, unknown>;
  } {
    const enrichedOld: Record<string, unknown> = {};
    const enrichedNew: Record<string, unknown> = {};

    for (const key of allKeys) {
      if (key === 'reviewScoreType') {
        continue;
      }

      const oldValueForKey = this.getOldValueForKey(key, oldValue, newValue);

      if (key === 'reviewScoreTypeId') {
        const { oldVal, newVal } = this.processReviewScoreTypeId(
          oldValue,
          newValue
        );
        enrichedOld[key] = oldVal;
        enrichedNew[key] = newVal;
      } else {
        enrichedOld[key] = this.enrichFieldValue(
          key,
          oldValueForKey,
          oldValue,
          newValue
        );
        enrichedNew[key] = this.enrichFieldValue(
          key,
          newValue[key],
          newValue,
          oldValue
        );
      }
    }

    return { enrichedOld, enrichedNew };
  }

  private buildModifiedFieldsWithAuditContext(params: {
    oldValue?: Record<string, unknown>;
    newValue: Record<string, unknown>;
    exclude?: string[];
  }): AuditFieldChange[] {
    const { oldValue = {}, newValue, exclude = [] } = params;

    const labelMap: Record<string, string> = {
      reviewType: 'Review Type',
      caseBehaviourId: 'Behavior',
      caseBehvaiorId: 'Behavior',
      reviewScoreTypeId: 'Review Score Type',
      dateModifiedByAdmin: 'Modified Date',
      requestChanges: 'Changes Requested',
      adminNotes: 'Admin Notes',
      reviewedByUserId: 'Reviewed By User',
      userNotes: 'User Notes',
      dateModifiedByUser: 'User Modified Date',
      changesApplied: 'Changes Applied',
    };
    const sensitiveFields: string[] = [];

    const allKeys = Array.from(
      new Set([...Object.keys(oldValue || {}), ...Object.keys(newValue || {})])
    );

    const { enrichedOld, enrichedNew } = this.buildEnrichedValues(
      allKeys,
      oldValue,
      newValue
    );

    const changedOld: Record<string, unknown> = {};
    const changedNew: Record<string, unknown> = {};

    const isEmpty = (value: unknown): boolean =>
      value === null || value === '' || value === undefined;

    for (const key of Object.keys(enrichedNew)) {
      if (exclude.includes(key)) {
        continue;
      }

      const oldVal = enrichedOld[key];
      const newVal = enrichedNew[key];

      if (oldVal !== newVal && !(isEmpty(oldVal) && isEmpty(newVal))) {
        changedOld[key] = oldVal;
        changedNew[key] = newVal;
      }
    }

    return this.auditContextService.buildFieldChanges(changedOld, changedNew, {
      sensitiveFields,
      labelMap,
      excludeFields: exclude,
    });
  }

  private handleError(error: ErrorResponse): void {
    this.errorMessage = error?.message;
    this.commonService.notify('error', this.errorMessage);
  }

  handleEmailSend(event: sendEmailPayload) {
    this.emailPayload = event;
  }

  sendEmail() {
    const emailPayload = {
      to: this.emailPayload.emails ?? [],
      subject: this.emailPayload.subject,
      body: this.emailPayload.body,
      isHtml: true,
      caseId: this.drawerParams?.caseDetail?.id ?? 0,
    };

    const modifiedFields = this.buildEmailAuditFields(emailPayload);
    (
      emailPayload as unknown as { modifiedFields?: AuditFieldChange[] }
    ).modifiedFields = modifiedFields;
    (
      emailPayload as unknown as { _modifiedFields?: AuditFieldChange[] }
    )._modifiedFields = modifiedFields;

    this.loaderService.show();
    const operation$ =
      this.fidelityService.sendEmailNotifications(emailPayload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            `${this.fidelityCheckLabel} emails sent successfully`
          );
          this.drawerService.closeDrawer(
            DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
          );
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }
  private buildEmailAuditFields(
    emailPayload: Record<string, unknown>
  ): AuditFieldChange[] {
    const labelMap: Record<string, string> = {
      to: 'Recipients',
      subject: 'Subject',
      body: 'Body',
      isHtml: 'HTML Format',
    };

    const sensitiveFields: string[] = [];

    const enrichedPayload = { ...emailPayload };
    if (
      enrichedPayload['isHtml'] !== null &&
      enrichedPayload['isHtml'] !== undefined
    ) {
      enrichedPayload['isHtml'] =
        enrichedPayload['isHtml'] === true ? 'Yes' : 'No';
    }

    return this.auditContextService.buildFieldChanges({}, enrichedPayload, {
      sensitiveFields,
      labelMap,
      excludeFields: [],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
